import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebFont from 'webfontloader';
import { parse, stringify } from 'query-string';
import { useBrowserFingerprint } from '@dev/ee-fingerprint';
import TagManager from '@sooro-io/react-gtm-module';
import { Helmet } from 'react-helmet';
import { omit, isEmpty } from 'lodash-es';
import Router from '@/services/Router';
import Auth from '@/services/Authentication';
import { SessionStartAuthWithDisposableToken } from '@/store/session/actions';
import { AccountGetCustomBrandingStart, AccountStartLoading, getAccountStore } from '@/store/account/actions';
import ScrollIntoView from '@/services/ScrollIntoView';
import Dialog from '@/services/Dialog';
import { ToastsProvider } from '@/services/ToastsProvider';
import { useNotifier } from '@/services/hooks/useNotifier';
import { OptionalIntercomProvider } from './services/IntercomProvider';
import { StorageKeysEnum } from './services/dictionaries/StorageKeys';
import { HomeOnboardingProvider } from './services/contexts/HomeScreenOnboarding';

interface AppProps {
    history?: any;
}

WebFont.load({
    google: {
        families: ['Nunito:400,600,700', 'Mulish', 'sans-serif'],
    },
});

const tagManagerArgs = {
    gtmId: GTMID,
    source: GTM_SERVER
};

//@ts-expect-error datalayer
if (DEVMODE === 'PRODUCTION' && !!GTMID && !window?.dataLayer) {
    TagManager.initialize(tagManagerArgs);
}

const ApplicationCore = (): JSX.Element => {
    useNotifier();

    return (
        <OptionalIntercomProvider>
            <Helmet>
                <title>Email Marketing - Dashboard</title>
            </Helmet>
            <ScrollIntoView>
                <HomeOnboardingProvider>
                    <Router />
                </HomeOnboardingProvider>
            </ScrollIntoView>
            <Dialog />
        </OptionalIntercomProvider>
    );
};

const App = (props: AppProps): JSX.Element => {
    const dispatch = useDispatch();
    const { history } = props;
    const account = useSelector(getAccountStore);
    let queryParams = parse(window?.location?.search, { decode: false });
    const fingerprint = useBrowserFingerprint();

    if (fingerprint) {
        localStorage.setItem(StorageKeysEnum.Fingerprint, fingerprint);
    }

    useEffect(() => {
        dispatch(AccountGetCustomBrandingStart());
        WebFont.load({
            google: {
                families: ['Nunito', 'Mulish', 'sans-serif'],
            },
        });

        if (queryParams.i) {
            const publicAccountId = queryParams.i as string;

            sessionStorage.setItem('subaccountemailtouse', publicAccountId);
            sessionStorage.setItem('parentName', 'Admin');

            queryParams = omit(queryParams, ['i']);

            if (isEmpty(queryParams)) {
                history.replace({ search: stringify(queryParams) });
                window.location.reload();
            }
        }

        if (queryParams.t) {
            const token = queryParams.t as string;
            dispatch(SessionStartAuthWithDisposableToken(token));
            queryParams = omit(queryParams, ['t']);

            if (!isEmpty(queryParams)) {
                history.replace({ search: stringify(queryParams) });
            }
        }

        if (!account.GeneralInfo && !account.accountLoading && Auth.isAuthenticated()) {
            dispatch(AccountStartLoading());
        }
    }, []);

    return (
        <ToastsProvider>
            <ApplicationCore />
        </ToastsProvider>
    );
};

export default App;
